import React, { useEffect } from "react"
import PropTypes from "prop-types"
import {
    Dialog,
    Paper,
    Grid,
    Typography,
    IconButton,
    Box,
} from "@mui/material"
import InfoDataPanel from "src/v2/components/InfoDataPanel/info-data-panel"
import { ReactComponent as OpenLinkIcon } from "src/v2/assets/icons/new-windows-icon.svg"
import { ReactComponent as CloseMenuIcon } from "src/v2/assets/icons/close-icon.svg"
import { ReactComponent as TrashIcon } from "src/v2/assets/icons/trash-icon.svg"
import AddIcon from "@mui/icons-material/Add"
import DataGridTable from "../../../../../components/Table/data-grid-table"
import { dispatchSnackbar } from "../../../../../components/SnackBar/snackbar-bus"
import theme from "src/v2/theme/theme"
import {normalizeHref} from "src/v2/utils/normalize-href"
import ButtonWithIcon from "../../../../../components/Button/Button"

const modalTableCellSx = {
    width: "100%",
    py: 1,
    fontSize: "14px !important",
    lineHeight: "1.35 !important",
    color: `${theme.palette.common.white} !important`,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",

    "&, & *": {
        fontSize: "14px !important",
        lineHeight: "1.35 !important",
        color: `${theme.palette.common.white} !important`,
    },
}

const modalTableSx = {
    border: "none",
    backgroundColor: "transparent",
    color: theme.palette.common.white,

    "& .MuiDataGrid-root": {
        border: "none",
        backgroundColor: "transparent",
        color: theme.palette.common.white,
        fontFamily: theme.typography.fontFamily,
    },

    "& .MuiDataGrid-main": {
        backgroundColor: "transparent",
    },

    "& .MuiDataGrid-withBorderColor": {
        borderColor: "transparent",
    },

    "& .MuiDataGrid-columnHeaders": {
        borderBottom: "none",
        backgroundColor: "transparent",
        minHeight: "56px !important",
        maxHeight: "56px !important",
        lineHeight: "56px !important",
    },

    "& .MuiDataGrid-columnHeader": {
        borderRight: "none",
        outline: "none !important",
        backgroundColor: "transparent",
    },

    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
        outline: "none",
    },

    "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitleContainerContent *": {
        color: `${theme.palette.common.white} !important`,
        fontSize: "14px !important",
        fontWeight: 700,
        textTransform: "uppercase",
        lineHeight: "1.2 !important",
        whiteSpace: "normal",
    },

    "& .MuiDataGrid-cell": {
        borderBottom: "none",
        borderRight: "none",
        color: `${theme.palette.common.white} !important`,
        fontSize: "14px !important",
        lineHeight: "1.35 !important",
        display: "flex",
        alignItems: "center",
        outline: "none !important",
    },

    "& .MuiDataGrid-cellContent, & .MuiDataGrid-cellContent *, & .MuiDataGrid-cell .MuiTypography-root, & .MuiDataGrid-cell p, & .MuiDataGrid-cell span, & .MuiDataGrid-cell a": {
        color: `${theme.palette.common.white} !important`,
        fontSize: "14px !important",
        lineHeight: "1.35 !important",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
    },

    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
        outline: "none",
    },

    "& .MuiDataGrid-row": {
        borderBottom: "none",
        minHeight: "56px !important",
    },

    "& .MuiDataGrid-virtualScrollerRenderZone .MuiDataGrid-row:nth-of-type(odd)": {
        backgroundColor: "rgba(255, 255, 255, 0.03)",
    },

    "& .MuiDataGrid-virtualScrollerRenderZone .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor: "#3A4056",
    },

    "& .MuiDataGrid-row:hover": {
        backgroundColor: "#444B63",
    },

    "& .MuiDataGrid-footerContainer": {
        borderTop: "none",
        color: theme.palette.common.white,
    },

    "& .MuiDataGrid-iconSeparator": {
        display: "none",
    },

    "& .MuiDataGrid-menuIconButton, & .MuiDataGrid-sortIcon, & .MuiDataGrid-filterIcon": {
        color: theme.palette.common.white,
    },

    "& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
        color: theme.palette.common.white,
    },

    "& .MuiIconButton-root": {
        color: theme.palette.common.white,
    },

    "& .MuiInputBase-root": {
        color: theme.palette.common.white,
    },

    "& .MuiInputBase-input": {
        color: `${theme.palette.common.white} !important`,
    },

    "& .MuiDataGrid-cell--editing": {
        backgroundColor: "#3A4056 !important",
        boxShadow: "none",
    },
}

const renderValue = (value) => {
    if (value === null || value === undefined || value === "") return "-"
    if (typeof value === "object") return JSON.stringify(value, null, 2)
    return value
}

const formatLinkLabel = (value) => {
    if (value === null || value === undefined || value === "") return "-"

    const valueString = String(value)

    return valueString.replace(/^https?:\/\//, "").replace(/^www\./, "")
}

const scrollbarSx = {
    scrollbarWidth: "thin",
    scrollbarColor: `${theme.palette.secondary.tertiary} transparent`,
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.white.main,
        borderRadius: "8px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: theme.palette.secondary.tertiary,
    },
}

const getFieldGridColumn = (field) => ({
    xxxs: `span ${field.xxxs ?? field.xs ?? 6}`,
    xs: `span ${field.xs ?? 6}`,
    sm: `span ${field.sm ?? 6}`,
    md: `span ${field.md ?? 3}`,
})

const LinkValue = ({ value, href, valueSx, valueVariant }) => {
    const normalizedHref = normalizeHref(href)

    if (!normalizedHref) {
        return (
            <Typography
                component="span"
                variant={valueVariant}
                sx={{
                    color: theme.palette.common.white,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    whiteSpace: "pre-line",
                    ...valueSx,
                }}
            >
                {renderValue(value)}
            </Typography>
        )
    }

    const linkLabel = formatLinkLabel(value || href)

    return (
        <Box
            component="a"
            href={normalizedHref}
            target="_blank"
            rel="noopener noreferrer"
            title={String(value || href)}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                width: "fit-content",
                maxWidth: "100%",
                color: theme.palette.icon.main,
                textDecoration: "none",
                cursor: "pointer",
                minWidth: 0,
                "&:hover": {
                    color: theme.palette.secondary.tertiary,
                    textDecoration: "underline",
                },
            }}
        >
            <Typography
                component="span"
                variant={valueVariant}
                sx={{
                    color: "inherit",
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    ...valueSx,
                }}
            >
                {linkLabel}
            </Typography>

            <OpenLinkIcon
                style={{
                    width: 14,
                    height: 14,
                    flexShrink: 0,
                    color: "currentColor",
                }}
            />
        </Box>
    )
}

LinkValue.propTypes = {
    value: PropTypes.any,
    href: PropTypes.string,
    valueSx: PropTypes.object,
    valueVariant: PropTypes.string,
}

LinkValue.defaultProps = {
    valueVariant: "body2",
}

const FieldBox = ({
    label,
    value,
    href,
    optionalText,
    withoutBorder = false,
    sx,
    labelSx,
    valueSx,
    optionalTextSx,
    labelVariant = "contraHeading",
    valueVariant = "body2",
    optionalTextVariant = "captionInfo",
}) => {
    return (
        <Box
            sx={{
                pl: withoutBorder ? 0 : 1.5,
                borderLeft: withoutBorder
                    ? "none"
                    : `1px solid ${theme.palette.input.borderColor.white70}`,
                width: "100%",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                ...sx,
            }}
        >
            <Typography
                variant={labelVariant}
                sx={{
                    color: theme.palette.primary.white70,
                    mb: 1,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    ...labelSx,
                }}
            >
                {label}
            </Typography>

            <LinkValue
                value={value}
                href={href}
                valueSx={valueSx}
                valueVariant={valueVariant}
            />

            {optionalText && (
                <Typography
                    variant={optionalTextVariant}
                    sx={{
                        color: theme.palette.common.white,
                        opacity: 0.6,
                        mt: 0.5,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        whiteSpace: "pre-line",
                        ...optionalTextSx,
                    }}
                >
                    {optionalText}
                </Typography>
            )}
        </Box>
    )
}

FieldBox.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    href: PropTypes.string,
    optionalText: PropTypes.node,
    withoutBorder: PropTypes.bool,
    sx: PropTypes.object,
    labelSx: PropTypes.object,
    valueSx: PropTypes.object,
    optionalTextSx: PropTypes.object,
    labelVariant: PropTypes.string,
    valueVariant: PropTypes.string,
    optionalTextVariant: PropTypes.string,
}

const DividedCell = ({
    label,
    value,
    href,
    optionalText,
    sx,
    labelSx,
    valueSx,
    optionalTextSx,
    labelVariant,
    valueVariant,
    optionalTextVariant,
}) => {
    return (
        <Box
            sx={{
                p: 1.5,
                minHeight: 58,
                minWidth: 0,
                width: "100%",
                height: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                background: `linear-gradient(146.22deg, ${theme.palette.transparent.white10} 0%, transparent 100%)`,
                overflow: "hidden",
                ...sx,
            }}
        >
            <FieldBox
                label={label}
                value={value}
                href={href}
                optionalText={optionalText}
                withoutBorder
                labelSx={labelSx}
                valueSx={valueSx}
                optionalTextSx={optionalTextSx}
                labelVariant={labelVariant}
                valueVariant={valueVariant}
                optionalTextVariant={optionalTextVariant}
            />
        </Box>
    )
}

DividedCell.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    href: PropTypes.string,
    optionalText: PropTypes.node,
    sx: PropTypes.object,
    labelSx: PropTypes.object,
    valueSx: PropTypes.object,
    optionalTextSx: PropTypes.object,
    labelVariant: PropTypes.string,
    valueVariant: PropTypes.string,
    optionalTextVariant: PropTypes.string,
}

const SectionTitle = ({ children, sx }) => {
    if (!children) return null

    return (
        <Typography
            sx={{
                mb: 2,
                fontSize: "18px !important",
                fontWeight: 400,
                color: theme.palette.common.white,
                ...sx,
            }}
        >
            {children}
        </Typography>
    )
}

SectionTitle.propTypes = {
    children: PropTypes.node,
    sx: PropTypes.object,
}

const getFieldValue = (field) => {
    if (
        field.value !== undefined &&
        field.value !== null &&
        field.value !== ""
    ) {
        return field.value
    }

    if (field.subfields?.length) {
        return field.subfields[0]?.label || field.subfields[0]?.value
    }

    return field.value
}

const getFieldOptionalText = (field) => {
    if (field.optionalText) return field.optionalText

    if (field.subfields?.length) {
        const firstSubfield = field.subfields[0]

        if (firstSubfield?.label && firstSubfield?.value) {
            return firstSubfield.value
        }
    }

    return field.optionalText
}

const getColumnField = (column) => column.field ?? column.key

const getColumnHeaderName = (column) => column.headerName ?? column.label

const renderModalTableCell = (column, params) => {
    const value = params.value

    if (column.type === "link") {
        const label = typeof value === "object" ? value.label : value
        const href = typeof value === "object" ? value.href : params.row.href

        return (
            <Typography
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                    ...modalTableCellSx,
                    color: "#FFFFFF !important",
                    textDecoration: "underline",
                    ...column.cellSx,
                }}
            >
                {label || "-"}
            </Typography>
        )
    }

    const renderedValue = column.renderCell
        ? column.renderCell(params)
        : column.formatter
            ? column.formatter(value, params.row)
            : value

    if (React.isValidElement(renderedValue)) {
        return (
            <Box
                sx={{
                    ...modalTableCellSx,
                    ...column.cellSx,
                }}
            >
                {renderedValue}
            </Box>
        )
    }

    return (
        <Typography
            sx={{
                ...modalTableCellSx,
                ...column.cellSx,
            }}
        >
            {renderValue(renderedValue)}
        </Typography>
    )
}

const buildModalTableColumns = (columns = []) => {
    return columns.map((column) => ({
        ...column,
        field: getColumnField(column),
        headerName: getColumnHeaderName(column),
        flex: column.flex ?? 1,
        minWidth: column.minWidth ?? (column.type === "link" ? 100 : 120),
        sortable: column.sortable ?? true,
        filterable: column.filterable ?? true,
        renderCell: (params) => renderModalTableCell(column, params),
    }))
}

const buildRows = (rows = []) =>
    rows.map((row, index) => ({
        ...row,
        id: row?.id ?? row?.ID ?? index,
    }))

const FieldsSection = ({ section }) => {
    return (
        <Box sx={{ mb: section.mb ?? 2, minWidth: 0, ...section.sx }}>
            <SectionTitle sx={section.titleSx}>{section.title}</SectionTitle>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
                    columnGap: section.columnGap ?? 2,
                    rowGap: section.rowGap ?? 2,
                    minWidth: 0,
                }}
            >
                {section.fields.map((field) => (
                    <Box
                        key={field.label}
                        sx={{
                            gridColumn: getFieldGridColumn(field),
                            minWidth: 0,
                            display: "flex",
                            ...field.gridSx,
                        }}
                    >
                        <FieldBox
                            label={field.label}
                            value={getFieldValue(field)}
                            href={field.href}
                            optionalText={getFieldOptionalText(field)}
                            withoutBorder={field.withoutBorder ?? false}
                            sx={field.fieldSx}
                            labelSx={field.labelSx}
                            valueSx={field.valueSx}
                            optionalTextSx={field.optionalTextSx}
                            labelVariant={field.labelVariant}
                            valueVariant={field.valueVariant}
                            optionalTextVariant={field.optionalTextVariant}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

FieldsSection.propTypes = {
    section: PropTypes.object.isRequired,
}

const getSplitGridColumns = (columns) => {
    return columns.map((column) => `minmax(0, ${column.md ?? 6}fr)`).join(" ")
}

const getSplitGridTemplateColumns = (section) => {
    if (section.gridTemplateColumns) return section.gridTemplateColumns

    return {
        xxxs: "1fr",
        md: getSplitGridColumns(section.columns),
    }
}

const SplitSection = ({ section }) => {
    return (
        <Box sx={{ mb: section.mb ?? 2, ...section.sx }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: getSplitGridTemplateColumns(section),
                    columnGap: section.columnGap ?? 3,
                    rowGap: section.rowGap ?? 2,
                    alignItems: "flex-start",
                    width: "100%",
                    minWidth: 0,
                }}
            >
                {section.columns.map((column, index) => (
                    <Box
                        key={`${column.title}-${index}`}
                        sx={{
                            minWidth: 0,
                            borderLeft: `1px solid ${theme.palette.input.borderColor.white70}`,
                            pl: 2,
                            boxSizing: "border-box",
                            ...column.sx,
                        }}
                    >
                        <SectionTitle sx={column.titleSx}>
                            {column.title}
                        </SectionTitle>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
                                columnGap: column.columnGap ?? 2,
                                rowGap: column.rowGap ?? 2,
                                minWidth: 0,
                            }}
                        >
                            {column.fields.map((field) => (
                                <Box
                                    key={field.label}
                                    sx={{
                                        gridColumn: getFieldGridColumn(field),
                                        minWidth: 0,
                                        ...field.gridSx,
                                    }}
                                >
                                    <FieldBox
                                        label={field.label}
                                        value={getFieldValue(field)}
                                        href={field.href}
                                        optionalText={getFieldOptionalText(field)}
                                        withoutBorder
                                        sx={field.fieldSx}
                                        labelSx={field.labelSx}
                                        valueSx={field.valueSx}
                                        optionalTextSx={field.optionalTextSx}
                                        labelVariant={field.labelVariant}
                                        valueVariant={field.valueVariant}
                                        optionalTextVariant={field.optionalTextVariant}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

SplitSection.propTypes = {
    section: PropTypes.object.isRequired,
}

const MatrixLockupTable = ({ columns, rows }) => {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xxxs: `repeat(${columns.length}, minmax(0, 1fr))`,
                    md: `48px repeat(${columns.length}, minmax(0, 1fr))`,
                },
                gap: 0,
                minWidth: 0,
            }}
        >
            <Box sx={{ display: { xxxs: "none", md: "block" } }} />

            {columns.map((column) => (
                <Typography
                    key={column}
                    sx={{
                        display: { xxxs: "none", md: "block" },
                        textAlign: "center",
                        fontSize: "16px !important",
                        color: theme.palette.common.white,
                        mb: 1,
                        minWidth: 0,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                    }}
                >
                    {column}
                </Typography>
            ))}

            {rows.map((row) => (
                <React.Fragment key={row.label}>
                    <Typography
                        sx={{
                            display: { xxxs: "none", md: "flex" },
                            fontSize: "16px !important",
                            color: theme.palette.common.white,
                            alignItems: "center",
                            minWidth: 0,
                        }}
                    >
                        {row.label}
                    </Typography>

                    {row.values.map((cell, index) => (
                        <DividedCell
                            key={`${row.label}-${index}`}
                            label={cell.label}
                            value={getFieldValue(cell)}
                            href={cell.href}
                            optionalText={getFieldOptionalText(cell)}
                            sx={{ minHeight: 58, ...cell.sx }}
                            labelSx={cell.labelSx}
                            valueSx={cell.valueSx}
                            optionalTextSx={cell.optionalTextSx}
                            labelVariant={cell.labelVariant}
                            valueVariant={cell.valueVariant}
                            optionalTextVariant={cell.optionalTextVariant}
                        />
                    ))}
                </React.Fragment>
            ))}
        </Box>
    )
}

MatrixLockupTable.propTypes = {
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired,
}

const MatrixSideGroups = ({ sideGroups }) => {
    if (!sideGroups?.length) return null

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            {sideGroups.map((group) => (
                <Box key={group.title} sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: "16px !important",
                            color: theme.palette.common.white,
                            mb: 2,
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                            ...group.titleSx,
                        }}
                    >
                        {group.title}
                    </Typography>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
                            columnGap: group.columnGap ?? 2,
                            rowGap: group.rowGap ?? 2,
                            minWidth: 0,
                        }}
                    >
                        {group.fields.map((field) => (
                            <Box
                                key={`${group.title}-${field.label}`}
                                sx={{
                                    gridColumn: getFieldGridColumn(field),
                                    minWidth: 0,
                                    ...field.gridSx,
                                }}
                            >
                                <FieldBox
                                    label={field.label}
                                    value={getFieldValue(field)}
                                    href={field.href}
                                    optionalText={getFieldOptionalText(field)}
                                    withoutBorder
                                    sx={field.fieldSx}
                                    labelSx={field.labelSx}
                                    valueSx={field.valueSx}
                                    optionalTextSx={field.optionalTextSx}
                                    labelVariant={field.labelVariant}
                                    valueVariant={field.valueVariant}
                                    optionalTextVariant={field.optionalTextVariant}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    )
}

MatrixSideGroups.propTypes = {
    sideGroups: PropTypes.array,
}

const MatrixSection = ({ section }) => {
    return (
        <Box sx={{ mb: section.mb ?? 1.5, ...section.sx }}>
            <SectionTitle sx={section.titleSx}>{section.title}</SectionTitle>

            <Grid container spacing={1} alignItems="flex-start">
                <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                    <MatrixLockupTable
                        columns={section.columns}
                        rows={section.rows}
                    />
                </Grid>

                <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                    <MatrixSideGroups sideGroups={section.sideGroups} />
                </Grid>
            </Grid>
        </Box>
    )
}

MatrixSection.propTypes = {
    section: PropTypes.object.isRequired,
}

const chunkArray = (items, size) => {
    const chunks = []

    for (let index = 0; index < items.length; index += size) {
        chunks.push(items.slice(index, index + size))
    }

    return chunks
}

const TableSection = ({ section }) => {
    const columns = buildModalTableColumns(section.columns)
    const rows = buildRows(section.rows ?? [])

    return (
        <DataGridTable
            columns={columns}
            rows={rows}
            disableDeleteColumn
            pageSize={25}
            autoRowHeight
            resetKey={`table-${section.title}`}
            sx={{
                ...modalTableSx,
                ...(section.tableSx ?? {}),
            }}
        />
    )
}

TableSection.propTypes = {
    section: PropTypes.object.isRequired,
}

const CardsSection = ({ section }) => {
    const infoDataSections = chunkArray(section.cards, 4).map(
        (cardsRow, rowIndex) => ({
            variant: "divided",
            itemMinHeight: section.itemMinHeight ?? 70,
            items: cardsRow.map((card, cardIndex) => ({
                key: `${card.title}-${rowIndex}-${cardIndex}`,
                label: card.title,
                formatter: () => getFieldValue(card),
                optionalTextFormatter: () => getFieldOptionalText(card),
                optionalTextOpacity: 0.6,
                optionalTextVariant: "captionInfo",
                sx: {
                    p: 1.5,
                    minHeight: section.itemMinHeight ?? 70,
                    ...card.sx,
                },
            })),
        })
    )

    return (
        <Box sx={{ mb: section.mb ?? 0, ...section.sx }}>
            <InfoDataPanel
                title={section.title}
                titleSx={{
                    mb: 0.5,
                    fontSize: "18px !important",
                    fontWeight: 400,
                    color: "common.white",
                    ...(section.titleSx ?? {}),
                }}
                data={{}}
                sections={infoDataSections}
            />
        </Box>
    )
}

CardsSection.propTypes = {
    section: PropTypes.object.isRequired,
}

const EditTableSection = ({ section }) => {
    useEffect(() => {
        if (section.alert?.open) {
            dispatchSnackbar(
                section.alert.message ?? "",
                section.alert.type ?? "info"
            )
        }
    }, [
        section.alert?.open,
        section.alert?.message,
        section.alert?.type,
    ])

    const deleteColumn = {
        field: "acoes",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        headerName: "AÇÕES",
        minWidth: 150,
        flex: 0.8,
        cellSx: {
            "& svg": {
                color: `${theme.palette.icon.main} !important`,
            },
            "& svg path": {
                fill: "none !important",
                stroke: `${theme.palette.icon.main} !important`,
            },
            "& svg line, & svg polyline, & svg rect, & svg circle": {
                fill: "none !important",
                stroke: `${theme.palette.icon.main} !important`,
            },
        },
        renderCell: (params) => (
            <ButtonWithIcon
                icon={<TrashIcon />}
                onClick={() => {
                    section.actions?.deleteRow?.(params.row)
                }}
            >
                EXCLUIR
            </ButtonWithIcon>
        ),
    }

    const rawColumns = section.disableDeleteRow
        ? section.columns
        : [...section.columns, deleteColumn]

    const columns = buildModalTableColumns(rawColumns)
    const rows = buildRows(section.rows ?? [])

    return (
        <Box
            sx={{
                mb: section.mb ?? 2,
                width: "100%",
                minWidth: 0,
                ...section.sx,
            }}
        >
            <SectionTitle sx={section.titleSx}>
                {section.title}
            </SectionTitle>

            {!section.disableAddRow && (
                <Box sx={{ mb: 1, mt: 1 }}>
                    <ButtonWithIcon
                        icon={<AddIcon />}
                        onClick={() => section.actions?.addRow?.()}
                    >
                        {section.btnAddTitle || "ADICIONAR NOVO"}
                    </ButtonWithIcon>
                </Box>
            )}

            <DataGridTable
                columns={columns}
                rows={rows}
                disableDeleteColumn
                pageSize={25}
                autoRowHeight={section.rowHeight === "auto"}
                rowHeight={section.rowHeight}
                resetKey={`edit-table-${section.title}`}
                loading={section.loading}
                processRowUpdate={async (newRow) => {
                    if (section.actions?.updateRow) {
                        await section.actions.updateRow(newRow)
                    }

                    return newRow
                }}
                onProcessRowUpdateError={(error) =>
                    console.error("Erro ao atualizar:", error)
                }
                sx={{
                    ...modalTableSx,
                    ...(section.tableSx ?? {}),
                }}
            />
        </Box>
    )
}

EditTableSection.propTypes = {
    section: PropTypes.object.isRequired,
}

const sectionComponents = {
    split: SplitSection,
    matrix: MatrixSection,
    cards: CardsSection,
    table: TableSection,
    "edit-table": EditTableSection,
}

const renderSection = (section, index) => {
    const key = `${section.type}-${section.title}-${index}`
    const Component = sectionComponents[section.type] || FieldsSection

    return <Component key={key} section={section} />
}

const EmissionDetailsModal = ({
    open,
    onClose,
    title,
    sections = [],
    empty = false,
    emptyText = "",
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            scroll="paper"
            PaperProps={{
                sx: {
                    m: {
                        xxxs: 0,
                        sm: "16px",
                        md: "32px",
                    },
                    width: {
                        xxxs: "100vw !important",
                        sm: "calc(100vw - 32px) !important",
                        md: "calc(100vw - 64px) !important",
                    },
                    maxWidth: {
                        xxxs: "100vw !important",
                        sm: "calc(100vw - 32px) !important",
                        md: "1280px !important",
                        lg: "1360px !important",
                        xlg: "1480px !important",
                    },
                    borderRadius: 0,
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    maxHeight: {
                        xxxs: "100vh",
                        sm: "calc(100vh - 48px)",
                    },
                    overflow: "hidden",
                },
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    p: { xxxs: 2, sm: 3 },
                    borderRadius: 0,
                    backgroundColor: "background.paper",
                    width: "100%",
                    maxHeight: { xxxs: "100vh", sm: "calc(100vh - 48px)" },
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                }}
            >
                <Box sx={{ mb: 2, pr: 5, flexShrink: 0 }}>
                    <Typography
                        sx={{
                            fontSize: "20px !important",
                            fontWeight: 400,
                            color: theme.palette.common.white,
                        }}
                    >
                        {title}
                    </Typography>

                    <IconButton
                        sx={{
                            position: "absolute",
                            top: { xxxs: 10, sm: 16 },
                            right: { xxxs: 10, sm: 16 },
                            color: theme.palette.common.white,
                            p: 0.5,
                            "&:hover": {
                                backgroundColor:
                                    theme.palette.transparent.white10,
                            },
                        }}
                        onClick={onClose}
                    >
                        <CloseMenuIcon
                            style={{ color: theme.palette.icon.main }}
                        />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        overflowY: "auto",
                        overflowX: "auto",
                        pr: 1,
                        minHeight: 0,
                        minWidth: 0,
                        ...scrollbarSx,
                    }}
                >
                    {empty ? (
                        <Box
                            sx={{
                                minHeight: "40px",
                                display: "flex",
                                alignItems: "flex-start",
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ color: theme.palette.common.white, mt: 2 }}
                            >
                                {emptyText}
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container direction="column">
                            {sections.map(renderSection)}
                        </Grid>
                    )}
                </Box>
            </Paper>
        </Dialog>
    )
}

EmissionDetailsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    sections: PropTypes.array,
    empty: PropTypes.bool,
    emptyText: PropTypes.string,
}

export default EmissionDetailsModal
